/* eslint-disable no-magic-numbers */
/**
 * Views and playback time tracker.
 */

import { loadTotalPlaybackTime, loadWatched120s, setTotalPlaybackTime, setWatched120s } from "@/common/utils/storage";
import { EventSource, metricsBus } from "@/common/buses";
import { IGif } from "@/common/services/types";
import { IEventSource } from "@/common/store/analyticsStore";

const MEANINGFUL_VIEW_DURATION = 5000;
const WATCHED_120S = 120000;

let totalPlayBackTime = loadTotalPlaybackTime() || 0;
let watched120s = loadWatched120s() || 0;

class PlaybackTracker {
  private gif: IGif | null;
  private startedAt: number | null;
  private sourceId: string | null;
  private username: string | null | undefined;
  private position: number | null | undefined;
  private source: EventSource | null | undefined;
  private startPoint: number;
  private playCount: number;

  public constructor() {
    this.gif = null;
    this.startedAt = null;
    this.startPoint = 0;
    this.sourceId = null;
    this.username = null;
    this.position = null;
    this.source = null;
    this.playCount = 1;
  }

  public stop(id?: string): void {
    if (id && (this.gif === null || this.gif.id !== id)) {
      return;
    }

    if (this.gif !== null && this.startedAt !== null) {
      const duration = Date.now() - this.startedAt;
      this.sendPlayback(
        this.gif,
        duration,
        this.sourceId,
        this.source,
        this.username,
        this.position,
        this.startPoint,
        this.playCount,
      );
      this.sendTotalPlayback(duration);
    }

    this.gif = null;
    this.startedAt = null;
  }

  public start(
    gif: IGif,
    source: IEventSource | null,
    username?: string | null,
    position?: number | null,
    startPoint?: number,
    playCount?: number,
  ): void {
    this.stop();

    this.gif = gif;
    this.startedAt = Date.now();
    this.sourceId = source?.id ?? null;
    this.username = username;
    this.position = position;
    this.source = source?.name;
    this.startPoint = startPoint ?? 0;
    this.playCount = playCount ?? 1;
    this.sendView(gif);
  }

  public setPlayCount(playCount: number): void {
    this.playCount = playCount;
  }

  private sendView(gif: IGif): void {
    console.debug(`[playback] gif ${gif.id} view.`);
  }

  private sendPlayback(
    gif: IGif,
    duration: number,
    sourceId: string | null,
    source?: EventSource | null,
    userName?: string | null,
    position?: number | null,
    startPoint: number = 0,
    playCount: number = 1,
  ): void {
    console.debug(`[playback] gif ${gif.id} playback, sourceId=${sourceId} duration=${duration} ms position=${position}`);

    const durationSeconds = duration / 1000;
    const percentViewed = Math.min(Math.round((durationSeconds / gif.duration) * 100), 100);

    metricsBus.emit("gifPlayback", {
      gif: gif.id,
      gifDuration: gif.duration,
      duration,
      sourceId,
      userName,
      percent: percentViewed,
      boosted: !!gif.cta,
      position: position,
      source: source ?? EventSource.Other,
      playCount,
      startPoint
    });

    if (duration >= MEANINGFUL_VIEW_DURATION) {
      metricsBus.emit("meaningfulGifView", {
        gifId: gif.id,
        percent: percentViewed,
      });

      const isReplay = duration >= (gif.duration * 1000) + MEANINGFUL_VIEW_DURATION;
      if (isReplay) {
        metricsBus.emit("gifReplay", {
          gifId: gif.id,
          tags: gif.tags
        });
      }
    }
  }

  private sendTotalPlayback(duration: number): void {
    const storageTotalPlayBackTime = loadTotalPlaybackTime() || 0;
    const storageWatched120s = loadWatched120s() || 0;
    let currentPlaybackTime = storageTotalPlayBackTime !== 0 ? storageTotalPlayBackTime : totalPlayBackTime;
    const currentWatched120s = storageWatched120s !== 0 ? storageWatched120s : watched120s;

    if (currentWatched120s === 1) {
      return;
    }

    currentPlaybackTime += duration;
    totalPlayBackTime = currentPlaybackTime;
    setTotalPlaybackTime(totalPlayBackTime);

    if (currentPlaybackTime >= WATCHED_120S) {
      metricsBus.emit("gifWatched120s");
      watched120s = 1;
      setWatched120s(watched120s);
    }
  }
}

export const playbackTracker = new PlaybackTracker();
